
/**
 * SearchBar.tsx
 *
 * SearchBar component for the Marketplace UI.
 * Provides a search input with debounced search, clear, and filter controls.
 *
 * Features:
 * - Debounced search input
 * - Mobile filter button with badge
 * - Clear and search icons
 * - Responsive and accessible UI
 *
 * @module SearchBar
 */

import React, { useState, useRef, useEffect } from 'react';
import { Icon } from '@ellucian/ds-icons/lib';
import { Badge, Grid } from '@ellucian/react-design-system/core';
import { trackSearch } from '../../utils/analytics';


/**
 * Props for the SearchBar component.
 * @property appliedSearch - The applied search string
 * @property setAppliedSearch - Callback to set the applied search
 * @property searchQuery - The current search query string
 * @property setSearchQuery - Callback to set the search query
 * @property filteredSolutionsCount - Number of filtered solutions (not used in this component)
 * @property filtersApplied - Whether any filters are applied
 * @property onBack - Callback for back navigation
 * @property onClearAll - Callback to clear all filters
 * @property onMobileFilterOpen - Callback to open the mobile filter modal
 * @property isMobile - Whether the UI is in mobile mode
 */
interface SearchBarProps {
  appliedSearch: string;
  setAppliedSearch: (value: string) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  filteredSolutionsCount: number;
  filtersApplied?: boolean;
  onBack: () => void;
  onClearAll: () => void;
  onMobileFilterOpen?: () => void;
  isMobile?: boolean;
}


/**
 * SearchBar component
 * Provides a search input with debounced search, clear, and filter controls.
 *
 * @param {SearchBarProps} props - Component props
 * @returns {JSX.Element}
 */
const SearchBar: React.FC<SearchBarProps> = ({
  appliedSearch,
  setAppliedSearch,
  searchQuery,
  setSearchQuery,
  filteredSolutionsCount,
  filtersApplied = false,
  onBack,
  onClearAll,
  onMobileFilterOpen,
  isMobile = false
}) => {
  // --- State and refs ---
  /** Whether the search input is focused */
  const [isFocused, setIsFocused] = useState(false);
  /** Ref to the search input element */
  const inputRef = useRef<HTMLInputElement>(null);
  /** Ref for debounce timeout */
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Applies the current search query as the applied search.
   */
  const handleSearch = () => {
    setAppliedSearch(searchQuery);    // Track search event when search button is clicked
    if (searchQuery.trim()) {
      trackSearch(searchQuery.trim());
    }  };

  /**
   * Handles input changes and debounces the search application.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // Set new timeout for debounced search
    debounceRef.current = setTimeout(() => {
      setAppliedSearch(value);
      // Track search event when search is applied
      if (value.trim()) {
        trackSearch(value.trim());
      }
    }, 300); // 300ms delay after user stops typing
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Reset search query if appliedSearch is cleared
  useEffect(() => {
    if (!appliedSearch) {
      setSearchQuery('');
    }
  }, [appliedSearch, setSearchQuery]);

  // --- Main render: search bar and mobile filter button ---
  return (
    <Grid container direction="column" spacing={1} style={{ width: '100%' }}>
      {/* Mobile Filter Button Row - Only show on mobile */}
      {isMobile && (
        <Grid item style={{ width: '100%', marginBottom: '12px' }}>
          <button
            onClick={onMobileFilterOpen}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              background: '#7100EB',
              color: '#FFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontWeight: 600,
              fontSize: '14px',
              width: 'fit-content',
            }}
          >
            <Icon name="filter" style={{ width: '16px', height: '16px' }} />
            Filters
            {/* Show badge if filters are applied */}
            {filtersApplied && (
              <Badge badgeContent="!" style={{ marginLeft: '4px' }} />
            )}
          </button>
        </Grid>
      )}

      {/* Search Form */}
      <Grid item style={{ width: '100%', position: 'relative' }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          style={{
            position: 'relative',
            width: '100%',
            borderRadius: '6px',
            border: '1px solid var(--Neutral-neutral-400, #B2B3B7)',
            background: 'var(--Neutral-neutral-100, #FFF)',
            display: 'flex',
            height: '40px',
            padding: '0 0 0 16px',
            alignItems: 'center',
            gap: '8px',
            alignSelf: 'stretch',
            boxSizing: 'border-box',
          }}
        >
          {/* Floating label for search input */}
          <label
            htmlFor="searchInput"
            style={{
              position: 'absolute',
              left: '16px',
              top: searchQuery || appliedSearch || isFocused ? '-8px' : '50%',
              transform: searchQuery || appliedSearch || isFocused ? 'none' : 'translateY(-50%)',
              fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontWeight: 400,
              fontSize: searchQuery || appliedSearch || isFocused ? '12px' : '16px',
              lineHeight: '120%',
              color: searchQuery || appliedSearch || isFocused ? '#5B5E65' : '#6b7280',
              backgroundColor: '#fff',
              padding: '0 4px',
              transition: 'all 0.2s ease',
              cursor: 'text',
            }}
          >
            Search
          </label>

          {/* Search input field */}
          <input
            id="searchInput"
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            style={{
              flex: 1,
              height: '100%',
              padding: '0 40px 0 1px', // Reduce left padding for cursor
              fontFamily: '"Proxima Nova", Montserrat, Aptos, Roboto',
              fontSize: '16px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '122%',
              color: 'var(--Neutral-neutral-600, #151618)',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              boxSizing: 'border-box',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          />

          {/* Search/clear icon (right side of input) */}
          <div style={{ marginTop: '24px' }}>
            {appliedSearch ? (
              <Icon
                name="close"
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px',
                  color: '#6B7280',
                }}
                onClick={() => {
                  setSearchQuery('');
                  setAppliedSearch('');
                  inputRef.current?.focus();
                  setIsFocused(true);
                }}
              />
            ) : (
              <Icon
                name="search"
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px',
                  color: 'var(--Neutral-neutral-500, #5B5E65)'
                }}
                onClick={handleSearch}
              />
            )}
          </div>
        </form>
      </Grid>
    </Grid>
  );
};

export default SearchBar;
